import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseOverflowedElementsParams {
  elementsIds: string[];
  containerWidth: number;
  direction?: 'left' | 'right';
  maxLength?: number;
  dependencies?: unknown;
  gap?: number;
}

/**
 * Хук для управления отображением элементов с учетом переполнения контейнера
 *
 * @param elementsIds - Массив идентификаторов элементов.
 * @param containerWidth - Ширина контейнера в пикселях.
 * @param direction - Направление скрытия элементов.
 * @param maxLength - Максимальное количество отображаемых элементов.
 * @param dependencies - Дополнительные внешние зависимости для скрытия элементов при неизменных elementsIds.
 * @param gap - Дополнительный отступ между элементами, не входящий в offsetWidth.
 * @returns Объект с результатами.
 */
export const useOverflowedElements = ({
  elementsIds,
  containerWidth,
  direction = 'right',
  maxLength,
  dependencies,
  gap = 0,
}: UseOverflowedElementsParams) => {
  const elementsRefs = useRef<Record<string, HTMLElement | null>>({});
  const [elementWidths, setElementWidths] = useState<Record<string, number>>(
    {},
  );
  const [visibleElements, setVisibleElements] = useState<string[]>(elementsIds);
  const [hiddenElements, setHiddenElements] = useState<string[]>([]);

  const setElementRef = (id: string) => (el: HTMLElement | null) => {
    elementsRefs.current[id] = el;
  };

  useLayoutEffect(() => {
    if (!elementsIds.length) return;

    const allRefsExist = elementsIds.every((id) => elementsRefs.current[id]);
    if (!allRefsExist) return;

    const widths: typeof elementWidths = {};
    elementsIds.forEach((id) => {
      const elementRef = elementsRefs.current[id];
      if (elementRef) widths[id] = elementRef.offsetWidth;
    });
    setElementWidths(widths);
  }, [elementsIds, dependencies]);

  useEffect(() => {
    const checkOverflow = () => {
      const availableWidth = containerWidth;

      let fitWidth = 0;
      const visible: string[] = [];

      const orderedIds =
        direction === 'left' ? [...elementsIds].reverse() : elementsIds;

      for (const id of orderedIds) {
        const elementWidth = elementWidths[id] ?? 0;
        const width = elementWidth + (visible.length > 0 ? gap : 0);
        const fitsByWidth = fitWidth + width <= availableWidth;
        const fitsByMaxLength = !maxLength || visible.length < maxLength;

        if (!fitsByWidth || !fitsByMaxLength) break;

        visible.push(id);
        fitWidth += width;
      }

      const orderedVisible =
        direction === 'left' ? [...visible].reverse() : visible;

      const hidden = elementsIds.filter((id) => !orderedVisible.includes(id));

      setVisibleElements(orderedVisible);
      setHiddenElements(hidden);
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [
    elementWidths,
    containerWidth,
    elementsIds,
    direction,
    maxLength,
    dependencies,
    gap,
  ]);

  return {
    visibleElements,
    hiddenElements,
    setElementRef,
  };
};
